import React, { useState } from 'react';
import { FieldConfig } from './FieldInput';
import { ArrowUpIcon, ArrowDownIcon, XIcon, EyeIcon, EyeOffIcon, Trash2Icon, PlusIcon, ChevronDownIcon } from 'lucide-react';
export type FieldConfigWithEnabled = FieldConfig & {
  enabled: boolean;
};
interface AdminConfigPanelProps {
  fields: FieldConfigWithEnabled[];
  availableFields: FieldConfig[];
  onToggleField: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onAddField: (field: FieldConfig) => void;
  onRemoveField: (id: string) => void;
  onClose: () => void;
}
export function AdminConfigPanel({
  fields,
  availableFields,
  onToggleField,
  onMoveUp,
  onMoveDown,
  onAddField,
  onRemoveField,
  onClose
}: AdminConfigPanelProps) {
  const [isAddingField, setIsAddingField] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const handleAdd = () => {
    const field = availableFields.find(f => f.id === selectedFieldId);
    if (field) {
      onAddField(field);
      setSelectedFieldId('');
      // Keep the add area open if there are more fields available
      if (availableFields.length <= 1) {
        setIsAddingField(false);
      }
    }
  };
  const handleCancelAdd = () => {
    setSelectedFieldId('');
    setIsAddingField(false);
  };
  return <div className="bg-slate-50 animate-in fade-in duration-200" data-id="element-3">
      {/* Field List */}
      <div className="p-4 max-h-[480px] overflow-y-auto" data-id="element-4">
        <div className="space-y-2" data-id="element-5">
          {fields.map((field, index) => <div key={field.id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${field.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-100/50 border-slate-200/50 opacity-75'}`} data-id="element-6">
              <div className="flex items-center gap-3 min-w-0" data-id="element-7">
                <button onClick={() => onToggleField(field.id)} className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${field.enabled ? 'text-indigo-600 hover:bg-indigo-50' : 'text-slate-400 hover:bg-slate-200'}`} aria-label={field.enabled ? 'Disable field' : 'Enable field'} data-id="element-8">
                  {field.enabled ? <EyeIcon className="w-4 h-4" data-id="element-9" /> : <EyeOffIcon className="w-4 h-4" data-id="element-10" />}
                </button>
                <div className="min-w-0" data-id="element-11">
                  <div className={`text-sm font-medium truncate ${field.enabled ? 'text-slate-900' : 'text-slate-500 line-through'}`} data-id="element-12">
                    {field.label}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mt-0.5" data-id="element-13">
                    {field.type}
                    {field.options && field.options.length > 0 && <span className="text-slate-300 ml-1" data-id="element-14">
                        · {field.options.length} options
                      </span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-15">
                <button onClick={() => onMoveUp(index)} disabled={index === 0} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors" aria-label="Move up" data-id="element-16">
                  <ArrowUpIcon className="w-4 h-4" data-id="element-17" />
                </button>
                <button onClick={() => onMoveDown(index)} disabled={index === fields.length - 1} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors" aria-label="Move down" data-id="element-18">
                  <ArrowDownIcon className="w-4 h-4" data-id="element-19" />
                </button>
                <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-20" />
                <button onClick={() => onRemoveField(field.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" aria-label={`Remove ${field.label}`} data-id="element-21">
                  <Trash2Icon className="w-4 h-4" data-id="element-22" />
                </button>
              </div>
            </div>)}

          {fields.length === 0 && !isAddingField && <div className="text-center py-6 text-sm text-slate-400" data-id="element-23">
              No fields configured. Add one below.
            </div>}
        </div>

        {/* Add Field Button / Dropdown */}
        {availableFields.length > 0 && <>
            {!isAddingField ? <button onClick={() => setIsAddingField(true)} className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-dashed border-indigo-200 rounded-lg transition-colors" data-id="element-24">
                <PlusIcon className="w-4 h-4" data-id="element-25" />
                Add Field
              </button> : <div className="mt-3 bg-indigo-50/70 border border-indigo-200 rounded-lg p-4 space-y-3 animate-in fade-in duration-150" data-id="element-26">
                <p className="text-xs font-semibold text-indigo-900 uppercase tracking-wider" data-id="element-27">
                  Add a Field
                </p>

                {/* Field Selector Dropdown */}
                <div className="relative" data-id="element-28">
                  <select value={selectedFieldId} onChange={e => setSelectedFieldId(e.target.value)} className="w-full text-sm border border-indigo-200 rounded-lg px-3 py-2.5 outline-none bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer" data-id="element-29">
                    <option value="" disabled data-id="element-30">
                      Select a field to add…
                    </option>
                    {availableFields.map(field => <option key={field.id} value={field.id} data-id="element-31">
                        {field.label} ({field.type})
                      </option>)}
                  </select>
                  <ChevronDownIcon className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-32" />
                </div>

                {/* Preview of selected field */}
                {selectedFieldId && (() => {
            const selected = availableFields.find(f => f.id === selectedFieldId);
            if (!selected) return null;
            return <div className="bg-white border border-indigo-100 rounded-lg p-3 flex items-center gap-3" data-id="element-33">
                        <div className="flex-1 min-w-0" data-id="element-34">
                          <div className="text-sm font-medium text-slate-900" data-id="element-35">
                            {selected.label}
                          </div>
                          <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mt-0.5" data-id="element-36">
                            {selected.type}
                            {selected.options && selected.options.length > 0 && <span className="text-slate-300 ml-1" data-id="element-37">
                                  · {selected.options.join(', ')}
                                </span>}
                            {selected.placeholder && <span className="text-slate-300 ml-1" data-id="element-38">
                                · placeholder: {selected.placeholder}
                              </span>}
                          </div>
                        </div>
                      </div>;
          })()}

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-1" data-id="element-39">
                  <button onClick={handleCancelAdd} className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-40">
                    Cancel
                  </button>
                  <button onClick={handleAdd} disabled={!selectedFieldId} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-41">
                    <PlusIcon className="w-3.5 h-3.5" data-id="element-42" />
                    Add Field
                  </button>
                </div>
              </div>}
          </>}

        {availableFields.length === 0 && fields.length > 0 && <div className="mt-3 text-center py-3 text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg" data-id="element-43">
            All available fields have been added
          </div>}
      </div>
    </div>;
}